import * as React from "react";

import {
  HeadingLarge,
  HeadingSmall,
  ParagraphMedium,
  ParagraphSmall,
} from "kanopi-components/typography";
import { PolicyStatus, QuoteState, RiskStateFilterEnum } from "kw/api/zeus";
import {
  QuoteDocumentPollList,
  StatelessDocumentList,
} from "kw/util/DocumentPollList/DocumentPollList";
import { navigate, useNavigate } from "@reach/router";
import { useFormatter, valueTypes } from "kw/util/useFormatter";
import { useTrackClaimStarted, useTrackPolicyViewed } from "../hooks/useTracking";

import { ActiveCoverSections } from "kw/util/CoverSection/ActiveCoverSections";
import { Block } from "baseui/block";
import { Button } from "kanopi-components/Button";
import { KIND as ButtonKind } from "kanopi-components/Button";
import { Callout } from "kanopi-components/Callout";
import { Checkbox } from "kanopi-components/Checkbox";
import { CoverLimitations } from "kw/util/CoverLimitations/CoverLimitations";
import { InactiveCoverSections } from "kw/util/CoverSection/InactiveCoverSections";
import { KIND } from "baseui/tag";
import { Kontainer } from "kanopi-components/Layout/Kontainer";
import { NavButton } from "kw/components/NavButton/NavButton";
import { Placeholder } from "kanopi-components/Placeholder";
import { PolicyFixedFooter } from "./PolicyFixedFooter";
import { PolicyStatusBanner } from "kanopi-components/Banner/PolicyStatusBanner";
import { Products } from "kw/quoting/components/Products";
import { QuestionFlowTypeKey } from "kw/components/QuestionFlow/QuestionFlowComponent";
import { QuotePrice } from "../../quoting/components/QuotePrice";
import { RiskState } from "kw/util/RiskState";
import { Sektion } from "kanopi-components/Layout/Sektion";
import { SidePadder } from "kanopi-components/Layout/SidePadder";
import { Tag } from "kanopi-components/Tag";
import { Triangle } from "kanopi-components/Triangle";
import { TwoColumnTable } from "kw/components/TabularDisplay/TwoColumnTable";
import { WidthContainer } from "kw/util/ChatFlow";
import { deref } from "kanopi-components/utils/primitives";
import { toCamelCase } from "kanopi-components/utils/string";
import { useGlobal } from "kanopi-components/useGlobal";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { DismissRenewalButton } from "kw/admin/components/DismissRenewalButton";
import { CreateRenewalButton } from "kw/admin/components/CreateRenewalButton";
import { StyledLink as Link } from "kanopi-components/Link/StyledLink";
import { AcceptRenewalButton } from "kw/admin/components/AcceptRenewalButton";

type TProps = {
  policy?: TPolicy;
  riskProfile?: TRiskProfile;
  riskData?: TRiskData;
  inactiveCovers?: TCoverSection[];
  coverSections?: TCoverSection[];
  loading?: boolean;
  editing?: boolean;
  priceLoading?: boolean;
  pricingFailed?: boolean;
  repriceQuote?: () => void;
  fetchPolicy?: () => void;
};

function isCoverEnabled(riskData: TRiskData) {
  return (cover: TCoverSection) =>
    deref(riskData, `covers.${toCamelCase(cover.key)}.enabled`);
}

const StatelessPolicy: React.FC<TProps> = (props) => {
  const {
    riskProfile = {},
    policy,
    coverSections = [],
    loading,
    editing,
    priceLoading,
    pricingFailed,
    repriceQuote,
    fetchPolicy,
  } = props;

  const [, baseTheme] = useStyletron();

  // Differentiate between enabled and disabled cover sections
  const splitCoverSections = (riskData, coverSections) => {
    const enabledCoverSections = coverSections.filter(isCoverEnabled(riskData));
    const disabledCoverSections = coverSections.filter(
      (c) => !isCoverEnabled(riskData)(c)
    );
    return [enabledCoverSections, disabledCoverSections];
  };

  const [
    autoRenew = {
      enabled: policy?.autoRenewEnabled,
    },
    setAutoRenew,
  ] = useGlobal(`autoRenew-update-${policy?.id}`);

  const trackPolicyViewed = useTrackPolicyViewed();
  const [updatedPolicy, setUpdatedPolicy] = React.useState({});
  const [showFixedFooter, setShowFixedFooter] = React.useState(true);

  const [enabledCoverSections, disabledCoverSections] = React.useMemo(
    () => splitCoverSections(riskProfile.riskData, coverSections),
    [riskProfile.riskData, coverSections]
  );
  const policyName = policy?.quote?.product?.name;
  const { formatDate, formatCurrency } = useFormatter();

  // TODO: DEV-4772 - Remove once all products are migrated to use exposures
  const useLegacyCoverSections = !riskProfile?.enabledExposures?.length;

  const handleUpdateRiskState = () => {
    navigate(`/policies/${policy?.id}/update/details`);
  };

  const handleUpdatePolicy = () => {
    navigate(`/policies/${policy.id}/update/knockout`);
  };

  const handleUpdatePolicyHolder = () => {
    navigate(`/policies/${policy.id}/update/details/policy_holder`);
  };

  const handleCancelPolicy = () => {
    navigate(`/policies/${policy.id}/cancel/start`);
  };

  const autoRenewToggle = (
    <Checkbox
      title="Enable auto renew"
      value={autoRenew?.enabled}
      checkmarkType="toggle"
      onChange={() => {
        setAutoRenew({
          enabled: !autoRenew?.enabled,
        });
      }}
      overrides={{
        Root: {
          props: {
            "data-testid": "autoRenewToggle",
          },
        },
      }}
    />
  );

  React.useEffect(() => {
    if (!loading) {
      trackPolicyViewed({ policy, riskProfile });
    }
  }, [loading, policy]);

  React.useEffect(() => {
    setUpdatedPolicy(riskProfile.riskData);
  }, [riskProfile.riskData]);

  const quoteText = React.useMemo(() => {
    if (policy?.latestRenewal?.quote?.state === QuoteState.ACCEPTED) {
      return "This quote has been accepted.";
    } else if (policy?.latestRenewal?.quote?.state === QuoteState.QUOTED) {
      return "This quote hasn't been accepted and your policy will expire on the renewal date above.";
    }
  }, [policy]);

  return (
    <Placeholder loading={loading}>
      <Kontainer>
        <Block marginTop={0} marginBottom="scale900">
          <HeadingSmall marginTop={0} marginBottom={0} center>
            Your {policyName?.toLowerCase() || "insurance"} for
          </HeadingSmall>
          {/* TODO: What goes in here? */}
          {policy?.quote?.userReference && (
            <HeadingLarge marginTop={0} marginBottom={0} center>
              {policy?.quote?.userReference}
            </HeadingLarge>
          )}
        </Block>
        {policy?.status != PolicyStatus.BOUND && (
          <Block display="flex" flexDirection="column" alignItems="center">
            <Tag kind={KIND.warning}>
              {policy?.statusDisplay} {formatDate(policy?.finishDate)}
            </Tag>
          </Block>
        )}

        <Sektion compact>
          {/* TODO: Abstract out for both updating, policies and quoting */}
          <QuotePrice
            periodicPrice={riskProfile?.periodicPrice}
            paymentFrequency={riskProfile?.paymentFrequency}
            loading={priceLoading}
            pricingFailed={pricingFailed}
            repriceQuote={repriceQuote}
            draftUpdate={policy?.draftUpdate}
            pricingOthers={riskProfile?.quotes?.[0]?.pricingOthers}
          />
          {riskProfile?.enabledExposures?.length ?? 0 > 0 ? (
            <Products
              riskProfile={riskProfile}
              quotes={[policy?.quote]}
              priceLoading={priceLoading}
              disabledCoverSections={disabledCoverSections}
              priceRiskProfile={null}
              editable={editing}
            />
          ) : null}
          {!editing && policy?.isPendingCancellation && (
            <Block display="flex" alignItems="center" flexDirection="column">
              <ParagraphMedium>
                Your policy is pending cancellation. If you would like to keep your
                policy, please contact us.
              </ParagraphMedium>
              <ParagraphMedium>
                You will be{" "}
                {policy?.latestCancellation?.amountDue < 0 ? "refunded" : "charged"}{" "}
                {formatCurrency(Math.abs(policy?.latestCancellation?.amountDue))} on{" "}
                {formatDate(policy?.latestCancellation?.effectiveDate)}.
              </ParagraphMedium>
            </Block>
          )}
          {!editing && (
            <ResponsiveButtons
              policy={policy}
              riskProfile={riskProfile}
              editing={editing}
            />
          )}
        </Sektion>

        {enabledCoverSections?.length > 0 && (
          <>
            <Block display={["none", "none", "flex"]} justifyContent="center">
              <Triangle $color={baseTheme.colors.backgroundTertiary} $size={50} />
            </Block>
            <Sektion backgroundColor={baseTheme.colors.backgroundTertiary} compact>
              <SidePadder>
                <HeadingLarge marginTop={0} center>
                  Cover included
                </HeadingLarge>
                <ActiveCoverSections
                  policyId={policy?.id}
                  coverSections={enabledCoverSections}
                  riskProfile={riskProfile}
                  prefix={`/policies/${policy?.id}/update`}
                  editable={editing}
                  finishedRoute={`/policies/${policy?.id}/update`}
                  disabled={loading}
                  onUpdatingCoverSection={(isUpdating) =>
                    setShowFixedFooter(!isUpdating)
                  }
                />
              </SidePadder>
            </Sektion>
          </>
        )}

        {editing && !!disabledCoverSections?.length && (
          <Sektion backgroundColor={baseTheme.colors.backgroundTertiary} compact>
            <SidePadder>
              <HeadingLarge marginTop={0} center>
                Add a cover
              </HeadingLarge>
              <InactiveCoverSections
                coverSections={disabledCoverSections}
                riskProfile={riskProfile}
                prefix={`/policies/${policy?.id}/update`}
                disabled={loading}
                policyId={policy?.id}
              />
            </SidePadder>
          </Sektion>
        )}

        {/* TODO: DEV-4772 - Remove once all products are migrated to use exposures */}
        {useLegacyCoverSections && (
          <Sektion>
            <SidePadder>
              <CoverLimitations
                limitations={
                  editing
                    ? policy?.draftUpdate?.quotes?.at(-1)?.limitations
                    : policy?.quote?.limitations
                }
              />
            </SidePadder>
          </Sektion>
        )}

        <Sektion compact>
          <SidePadder>
            {editing ? (
              policy?.draftUpdate?.riskProfile?.id && (
                <QuoteDocumentPollList
                  riskProfileId={policy?.draftUpdate?.riskProfile?.id}
                  globalDataKey={QuestionFlowTypeKey.update}
                  shouldPollDocuments={false}
                />
              )
            ) : (
              <StatelessDocumentList documents={policy?.documents} />
            )}
          </SidePadder>
        </Sektion>

        {riskProfile.riskData?.policyHolder && (
          <Sektion compact>
            <SidePadder>
              <HeadingLarge marginTop={0} center>
                Policyholder
              </HeadingLarge>
              <RiskState
                disabled={!editing}
                riskProfileId={riskProfile.id}
                riskData={riskProfile.riskData}
                buttonText="Edit"
                filterBy={RiskStateFilterEnum.POLICY_HOLDER}
                update={editing ? handleUpdatePolicyHolder : null}
              />
            </SidePadder>
          </Sektion>
        )}

        <Sektion compact>
          <SidePadder>
            <HeadingLarge marginTop={0} center>
              The details you provided
            </HeadingLarge>
            <RiskState
              disabled={!editing}
              riskProfileId={riskProfile.id}
              riskData={riskProfile.riskData}
              buttonText="Edit"
              update={editing ? handleUpdateRiskState : null}
            />
          </SidePadder>
        </Sektion>

        {policy &&
        !policy.isPendingCancellation &&
        (policy.latestRenewal?.quote?.isBound === false ||
          policy.isWithinRenewalPeriod) ? (
          <Sektion compact>
            <SidePadder>
              <HeadingLarge marginTop={0} center>
                Your upcoming renewal
              </HeadingLarge>
              {policy.latestRenewal ? (
                <>
                  <TwoColumnTable
                    id="riskState-renewalInformation"
                    columnPadding={baseTheme.sizing.scale500}
                    rows={[
                      {
                        label: "Amount due",
                        value: policy?.latestRenewal?.quote?.riskProfile?.periodicPrice,
                        type: valueTypes.Currency,
                      },
                      {
                        label: "Renewal date",
                        value: policy?.finishDate,
                        type: valueTypes.Date,
                      },
                    ]}
                    typography={baseTheme.typography.ParagraphSmall}
                  />
                  {quoteText ? (
                    <ParagraphSmall center={true}>{quoteText}</ParagraphSmall>
                  ) : null}
                  <Block display="flex" justifyContent="center">
                    {policy.latestRenewal?.canAccept ? (
                      <AcceptRenewalButton
                        key={0}
                        renewalQuoteId={policy.latestRenewal?.id}
                        fetchPolicy={fetchPolicy}
                        overrides={{
                          BaseButton: {
                            style: { marginRight: baseTheme.sizing.scale600 },
                          },
                        }}
                      >
                        Accept
                      </AcceptRenewalButton>
                    ) : null}
                    {policy.latestRenewal?.canDiscard ? (
                      <DismissRenewalButton
                        key={0}
                        renewalQuoteId={policy.latestRenewal?.id}
                        fetchPolicy={fetchPolicy}
                      >
                        Decline
                      </DismissRenewalButton>
                    ) : null}
                  </Block>
                  {policy?.latestRenewal?.quote?.documents && (
                    <Block
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      flexDirection="column"
                      marginBottom={baseTheme.sizing.scale100}
                    >
                      <>
                        <HeadingSmall marginBottom={0} center>
                          Your renewal documents
                        </HeadingSmall>
                        <ul style={{ listStyleType: "none" }}>
                          {policy?.latestRenewal?.quote?.documents.map((document_) => (
                            <li key={document_.id}>
                              <Link href={document_.url} target="_blank">
                                {document_.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </>
                    </Block>
                  )}
                </>
              ) : (
                <Block
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ParagraphSmall center={true}>
                    Your policy is expiring soon but you don&apos;t yet have a quote for
                    renewal.
                    <br />
                    You can create one now.
                  </ParagraphSmall>
                  <CreateRenewalButton
                    key={0}
                    policyId={policy.id}
                    fetchPolicy={fetchPolicy}
                  />
                </Block>
              )}
            </SidePadder>
          </Sektion>
        ) : null}

        {policy?.quote?.product?.autoRenewAllowed &&
          policy?.status !== PolicyStatus.CANCELLED &&
          !policy?.isPendingCancellation && (
            <Sektion compact>
              <HeadingLarge marginTop={0} center>
                Auto renew
              </HeadingLarge>
              <TwoColumnTable
                id="riskState-autoRenewalToggle"
                columnPadding={baseTheme.sizing.scale500}
                rowPadding={baseTheme.sizing.scale0}
                rows={[
                  {
                    label: "Auto renew",
                    value: autoRenew?.enabled,
                    type: editing ? "Element" : valueTypes.Boolean,
                    element: autoRenewToggle,
                  },
                ]}
                typography={baseTheme.typography.ParagraphSmall}
              />
              {editing && (
                <Block display="flex" justifyContent="center">
                  <Callout>
                    Turning off auto renew will automatically disable any future
                    renewals that are currently in progress.
                  </Callout>
                </Block>
              )}
            </Sektion>
          )}
        {!editing && policy?.cancellationAllowed && (
          <Block display="flex" justifyContent="center">
            <Button
              size={Button.SIZE.mini}
              kind={Button.KIND.tertiary}
              overrides={{
                BaseButton: {
                  style: {
                    textDecoration: "underline",
                    textTransform: "uppercase",
                  },
                },
              }}
              onClick={handleCancelPolicy}
            >
              Cancel this policy
            </Button>
          </Block>
        )}
      </Kontainer>
      {editing && showFixedFooter && (
        <PolicyFixedFooter>
          <NavButton
            disabled={!updatedPolicy || Object.keys(updatedPolicy).length === 0}
            onClick={handleUpdatePolicy}
            data-testid="applyPolicyUpdateButton"
          >
            I&apos;ve made all my changes
          </NavButton>
        </PolicyFixedFooter>
      )}
    </Placeholder>
  );
};

type TResponsiveButtons = {
  policy: TPolicy;
  editing: boolean;
};

function ResponsiveButtons({ policy, editing }: TResponsiveButtons) {
  const [, theme] = useStyletron();
  useTrackClaimStarted();

  const handleMakeClaim = () => {
    if (policy?.claimsAllowed) {
      navigate(`/claims/make-a-claim/${policy.id}/intro`);
    } else {
      navigate("/claims/make-a-claim/error");
    }
  };
  const navigate = useNavigate();
  const handleUpdatePolicy = () => {
    navigate(`/policies/${policy.id}/update/start`);
  };

  const skipPayments = policy?.quote?.product?.skipPayments || false;

  return (
    <>
      {!editing && (
        <PolicyFixedFooter
          blockOverrides={{
            paddingLeft: ["scale600", "scale600", "scale1600"],
            paddingRight: ["scale600", "scale600", "scale1600"],
            paddingTop: ["scale600", "scale600", "scale1600"],
            paddingBottom: ["scale600", "scale600", "0"],
          }}
        >
          <WidthContainer>
            <PolicyStatusBanner
              marginBottom={theme.sizing.scale600}
              policyId={policy?.id}
              policyLatestUpdate={policy?.latestUpdate}
            />
          </WidthContainer>

          {policy?.updateAllowed && (
            <NavButton
              kind={ButtonKind.secondary}
              onClick={handleUpdatePolicy}
              data-testid="policyUpdateButton"
            >
              Update your policy
            </NavButton>
          )}

          {policy?.quote?.product.claimsAllowed && (
            <NavButton
              onClick={handleMakeClaim}
              data-testid={`policyMakeClaimButton-${policy?.id}`}
            >
              Make a claim
            </NavButton>
          )}
        </PolicyFixedFooter>
      )}
    </>
  );
}

export { StatelessPolicy };
